/** Formatting helpers. All measured values are rendered monospaced elsewhere; here we
 *  only decide digits and dashes. `—` means "no value", never "0". */

const DASH = "—";

const dayMonth = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" });
const dayMonthShort = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "short" });
const fullDate = new Intl.DateTimeFormat("ru-RU", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const dateTime = new Intl.DateTimeFormat("ru-RU", {
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});
const timeOfDay = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" });
const timeOfDaySeconds = new Intl.DateTimeFormat("ru-RU", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});
const dayMonthYear = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/** ISO date (or datetime) -> Date, or null. Never throws: bad data must render, not crash. */
export const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const iso = value.length === 10 ? `${value}T00:00:00Z` : value;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const toIsoDate = (value: Date): string => value.toISOString().slice(0, 10);

/**
 * Today in the LOCAL calendar. `toISOString()` is UTC, so east of UTC every evening the
 * default analysis date and the `max` of the observation date input were yesterday — the
 * operator could not record what they had just seen.
 */
export const todayIso = (): string => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};

export const formatDate = (value: string | null | undefined): string => {
  const parsed = parseDate(value);
  return parsed ? fullDate.format(parsed) : DASH;
};

export const formatDayMonth = (value: string | null | undefined): string => {
  const parsed = parseDate(value);
  return parsed ? dayMonth.format(parsed) : DASH;
};

/**
 * "12 и 18 октября" — the month is printed once, by the locale's own range logic.
 * `${formatDayMonth(a)} по ${formatDayMonth(b)}` printed it twice ("с 12 октября по 18
 * октября"); `formatRange` collapses it, and the dash is swapped for "и" so the sentence
 * around it reads as prose ("между 12 и 18 октября") instead of as an axis label.
 */
export const formatDayMonthSpan = (
  from: string | null | undefined,
  to: string | null | undefined,
): string => {
  const start = parseDate(from);
  const end = parseDate(to);
  if (!start || !end) return formatDayMonth(from ?? to);
  return dayMonth.formatRange(start, end).replace(/\s*[–—-]\s*/, " и ");
};

/**
 * «30 июля, 14:12» — тот же день и месяц, что и везде на продуктовой поверхности, плюс время.
 * Нужен ровно в одном месте: в списке прошлых анализов, когда два прогона попали на одну дату
 * и строки различает только час (UX_SPEC §6.5). Дата берётся из `analysis_date`, время — из
 * `started_at`, поэтому аргумента два: у первого времени нет вовсе.
 *
 * День печатает `formatFull`, а не `formatDayMonth`: прошлогодний прогон в истории без года
 * читался свежим — та же двусмысленность, ради которой `formatFull` существует.
 * `withSeconds` — для пары прогонов, попавших в одну МИНУТУ (батч, сид): «4 сентября, 18:15»
 * дважды с противоположными ответами не давало понять, какая строка новее (run-time-seconds).
 */
export const formatDayMonthTime = (
  date: string | null | undefined,
  startedAt: string | null | undefined,
  withSeconds = false,
): string => {
  const day = formatFull(date);
  const parsed = parseDate(startedAt);
  if (!parsed) return day;
  return `${day}, ${(withSeconds ? timeOfDaySeconds : timeOfDay).format(parsed)}`;
};

/**
 * «30 июля», а в чужом году — «30 июля 2025».
 *
 * ОДИН ФОРМАТ ДАТЫ НА ВСЮ ПРОДУКТОВУЮ ПОВЕРХНОСТЬ. `formatDate` печатает «30.07.2026», и
 * рядом с «ответ 21 июля» в ростере и «сошла между 12 и 18 октября» в вердикте это второй
 * словарь дат на одном экране: читатель обязан переводить одну запись в другую, чтобы понять,
 * что речь про один день. Год — не украшение и не всегда: в сезонном продукте девять дат из
 * десяти лежат в текущем году, и печатать его на каждой значит просить прочитать лишнее.
 * Появляется он ровно тогда, когда без него дата двусмысленна.
 *
 * Численный `formatDate` остаётся живым — за ним ярус-3 (служебная панель, сверка, журналы),
 * где даты сравнивают глазами по колонке и словесный месяц мешает.
 */
export const formatFull = (value: string | null | undefined): string => {
  const parsed = parseDate(value);
  if (!parsed) return DASH;
  if (parsed.getFullYear() === new Date().getFullYear()) return dayMonth.format(parsed);
  // Локаль дописывает канцелярское « г.» («30 июля 2025 г.»). Год здесь различает эпоху, а
  // «г.» не различает ничего: это делопроизводственная помета, и на продуктовой поверхности,
  // где та же дата стоит в строке ростера и в подписи к фото, она только длиннее.
  return dayMonthYear.format(parsed).replace(/\s*г\.$/, "");
};

/** `formatFull` плюс время: «30 июля, 15:53». Один аргумент — здесь дата и время в одном ISO. */
export const formatFullTime = (value: string | null | undefined): string => {
  const parsed = parseDate(value);
  if (!parsed) return DASH;
  return `${formatFull(value)}, ${timeOfDay.format(parsed)}`;
};

export const formatDayMonthShort = (value: string | null | undefined): string => {
  const parsed = parseDate(value);
  return parsed ? dayMonthShort.format(parsed).replace(".", "") : DASH;
};

export const formatDateTime = (value: string | null | undefined): string => {
  const parsed = parseDate(value);
  return parsed ? dateTime.format(parsed) : DASH;
};

export const daysBetween = (from: string | null, to: string | null): number | null => {
  const a = parseDate(from);
  const b = parseDate(to);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
};

export const shiftIsoDate = (value: string, deltaDays: number): string => {
  const parsed = parseDate(value) ?? new Date();
  parsed.setUTCDate(parsed.getUTCDate() + deltaDays);
  return toIsoDate(parsed);
};

export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const num = (value: unknown, digits = 2): string =>
  isFiniteNumber(value) ? value.toFixed(digits) : DASH;

export const signed = (value: unknown, digits = 2): string =>
  isFiniteNumber(value) ? `${value >= 0 ? "+" : ""}${value.toFixed(digits)}` : DASH;

export const percent = (fraction: unknown, digits = 0): string =>
  isFiniteNumber(fraction) ? `${(fraction * 100).toFixed(digits)}%` : DASH;

export const percentValue = (value: unknown, digits = 0): string =>
  isFiniteNumber(value) ? `${value.toFixed(digits)}%` : DASH;

export const integer = (value: unknown): string =>
  isFiniteNumber(value) ? String(Math.round(value)) : DASH;

export const hectares = (value: unknown, digits = 1): string =>
  isFiniteNumber(value) ? value.toFixed(digits) : DASH;

export const dash = DASH;
